import i18n from "i18next";

import { APIErrorResponse, EntityAttribute } from "shared/api/api";
import {
  getSchemaDefinition,
  listSchemaDataAttributes,
  listSchemaDefinitions,
} from "shared/api/schemaDefinitions/api";
import { SchemaDefinition } from "shared/api/schemaDefinitions/types";
import {
  GENERIC_FILTER,
  SMART_GROUPS_V2_FILTER,
} from "shared/filterDefinitions";
import { SchemaEntryOverride, SchemaRelations } from "shared/schemas/types";
import { getGroupBySelectOptions } from "shared/schemas/utils/group-by-utils";
import {
  buildSchemaRelationEndpoint,
  buildSchemaValuesEndpoint,
  createMinimalSchema,
  getAttributeMap,
  getDisplayLabelFunction,
  getFilterFunction,
  getSchemaEntryFunction,
  getSchemaForAccessorFunction,
  getSchemaFromAttributes,
} from "shared/schemas/utils/schema-utils";

import { FilterType } from "features/ui/Filters/types";
import { SchemaEntry } from "features/ui/MaterialTable";

import { getSchemaNameFilter } from "./utils";

const SMART_GROUPS_ATTRIBUTE_IDS = ["smartGroups", "smart_group_ids"];

// Performs the full client-side schema build for a single schema definition:
// the definition itself, its data attributes, its relations, and the
// attributes of every related schema. This is the expensive multi-request
// orchestration the cache exists to run at most once per definition.
const fetchSchemaDefinitionSchema = async (
  schemaDefinitionId: string,
  // When resolved by name, the definition has already been fetched (the name
  // lookup returns the full definition). Reusing it here avoids a redundant
  // getSchemaDefinition request for the same definition.
  prefetchedDef?: SchemaDefinition
) => {
  const schemaDef =
    prefetchedDef ?? (await getSchemaDefinition(schemaDefinitionId));

  const attributes = await listSchemaDataAttributes({
    tableType: schemaDef.tableType,
    name: schemaDef.name,
  });

  const relationSchemas: SchemaRelations = {};
  const groupByRelations: SchemaRelations = {};
  const oneToManySchemas: Record<string, SchemaEntry[]> = {};

  const valuesEndpoint = buildSchemaValuesEndpoint(
    schemaDef.tableType,
    schemaDef.name
  );

  const enabledRelationKeys = attributes
    .filter((a) => a.relationEndpoint)
    .map((a) => a.ID);

  // Relations (with related schema + column pairs) are preloaded on the
  // definition, so no separate listSchemaRelations request is needed. The hook
  // exposes the full set; schema-building below only considers relations tied
  // to a relation attribute.
  const schemaRelations = schemaDef.relations ?? [];

  const buildableRelations =
    enabledRelationKeys.length > 0
      ? schemaRelations.filter((r) => enabledRelationKeys.includes(r.key))
      : schemaRelations;

  // Fetch every related schema's attributes in parallel rather than serially —
  // build latency would otherwise scale linearly with the relation count, and
  // this build gates the first paint of the schema pages.
  const builtRelations = (
    await Promise.all(
      buildableRelations.map(async (relation) => {
        const relatedDef = relation.relatedSchema;
        if (!relatedDef) return undefined;

        // key must match attribute.relationEndpoint
        const endpointKey = buildSchemaRelationEndpoint(
          relatedDef.tableType,
          relatedDef.name
        );
        const relatedValuesEndpoint = buildSchemaValuesEndpoint(
          relatedDef.tableType,
          relatedDef.name
        );

        const relatedAttributes = await listSchemaDataAttributes({
          tableType: relatedDef.tableType,
          name: relatedDef.name,
        });
        // A related schema's own one-to-many relations can't be resolved one
        // level deeper — the join returns the related row's plain columns only
        // — so they would emit as an always-empty "<Relation> > <Relation>"
        // column and an unusable filter.
        const relatedSchema = createMinimalSchema(
          relatedAttributes.filter((a) => a.type !== "array[relation]"),
          GENERIC_FILTER,
          {},
          {},
          relatedValuesEndpoint
        );

        // createMinimalSchema leaves groupBySelectOptions unset, which would
        // silently drop this relation from the parent schema's group-by
        // options. One level deep only — the related schema's own relations
        // aren't fetched here, so its relation attributes are excluded: they
        // would otherwise emit as bare leaves with incomplete dotted paths.
        if (!relation.isOneToMany) {
          const groupBySelectOptions = getGroupBySelectOptions(
            relatedAttributes.filter((a) => !a.relationEndpoint)
          );
          if (groupBySelectOptions.length > 0) {
            relatedSchema.groupBySelectOptions = groupBySelectOptions;
          }
        }

        return { relation, endpointKey, relatedSchema };
      })
    )
  ).filter((r): r is NonNullable<typeof r> => Boolean(r));

  for (const { relation, endpointKey, relatedSchema } of builtRelations) {
    if (relation.isOneToMany) {
      oneToManySchemas[endpointKey] = relatedSchema.schema;
    } else {
      relationSchemas[endpointKey] = relatedSchema;
      groupByRelations[endpointKey] = relatedSchema;
    }
  }

  const attachmentVirtualAttributeIDs = new Set<string>([
    "attachmentCount",
    "hasAttachments",
    "otherCount",
    ...(schemaDef.config?.attachments?.buckets ?? []).map(
      (b) => `${b.name}Count`
    ),
    ...(schemaDef.config?.attachments?.slots ?? []).map(
      (s) => `hasSlot_${s.name}`
    ),
  ]);
  const isAttachmentVirtualAttribute = (attribute: EntityAttribute): boolean =>
    attachmentVirtualAttributeIDs.has(attribute.ID) ||
    (attribute.virtual === true &&
      (attribute.ID === "attachmentCount" ||
        attribute.ID === "otherCount" ||
        attribute.ID.startsWith("hasSlot_") ||
        attribute.ID.endsWith("Count")));

  // getSchemaFromAttributes requires type === "relation" to expand relation
  // attributes. Keep the historical normalization for non-array relation
  // attributes, but preserve attachment virtual fields that use relationEndpoint
  // as a drill-down target while retaining numeric/boolean filter semantics.
  // A many-to-one relation's own sorting flag gates every column spread out
  // from it, so it is switched on and each column follows its own flag.
  const normalizedAttributes = attributes.map((a) => {
    if (
      !a.relationEndpoint ||
      a.type === "array[relation]" ||
      isAttachmentVirtualAttribute(a)
    ) {
      return a;
    }

    return relationSchemas[a.relationEndpoint]
      ? { ...a, type: "relation", sorting: true }
      : { ...a, type: "relation" };
  });

  const attributeMap = getAttributeMap(normalizedAttributes);
  const getDisplayLabel = getDisplayLabelFunction(attributeMap);
  const getFilter = getFilterFunction(
    GENERIC_FILTER,
    attributeMap,
    valuesEndpoint
  );

  // The smart-groups attribute has no schema-definition relation nor a values
  // endpoint, so the generic filter built from it would expose no usable
  // operators. Swap in the dedicated smart-groups filter (v2 API backed),
  // scoped to this schema's event type: all schemas share one smart-groups
  // table, so without the scope same-named groups of other schemas show up. The
  // override has to be keyed by the backend attribute ID, which is spelled
  // either way depending on the schema definition; the filter itself always
  // uses fieldName "smartGroups" for the query and name lookup.
  const smartGroupsAttributeID = SMART_GROUPS_ATTRIBUTE_IDS.find(
    (id) => attributeMap?.[id]
  );

  const schemaOverrides: SchemaEntryOverride =
    schemaDef.smartGrouping && smartGroupsAttributeID
      ? {
          [smartGroupsAttributeID]: {
            schemaOverride: {
              filter: SMART_GROUPS_V2_FILTER({
                eventType: schemaDef.name,
                fieldName: "smartGroups",
                fieldNameForAPI: "ID",
                label: getDisplayLabel(
                  smartGroupsAttributeID,
                  i18n.t("common:columns.smart_groups")
                ),
                disableArbitraryText: true,
              }),
            },
          },
        }
      : {};

  const getSchemaEntry = getSchemaEntryFunction(getFilter, attributeMap);

  const rawSchema = getSchemaFromAttributes(
    normalizedAttributes,
    getSchemaEntry,
    schemaOverrides,
    relationSchemas
  );

  // Attach one-to-many relationship schemas directly to their schema entries
  // so ExistsFilter can access them without needing a separate context. Also
  // force-disable the min/max aggregate filter options on every entry — the
  // "Highest value" / "Lowest value" select options don't make sense for the
  // dynamic-schema filter UIs for now
  const schema: SchemaEntry[] = rawSchema.map((entry) => {
    const ep = entry.filter?.relationEndpoint;
    // A one-to-many relation's cell is always empty, so it is never a table
    // column — even when it is not filterable or its related schema is missing.
    const withTableVisibility =
      attributeMap?.[entry.accessor]?.type === "array[relation]"
        ? { ...entry, hideInTable: true }
        : entry;
    const withRelation =
      ep && oneToManySchemas[ep]
        ? {
            ...withTableVisibility,
            relationSchema: oneToManySchemas[ep].map((relEntry) =>
              relEntry.filter
                ? {
                    ...relEntry,
                    filter: { ...relEntry.filter, enableMinMaxFilters: false },
                  }
                : relEntry
            ),
          }
        : withTableVisibility;

    if (!withRelation.filter) return withRelation;

    return {
      ...withRelation,
      filter: { ...withRelation.filter, enableMinMaxFilters: false },
    };
  });

  // Many-to-one relation attributes are expanded into "<relation>.<column>" entries above, which
  // can only ask about the parent's column values. Add an entry for the relation key itself so the
  // relation can be filtered on whether the parent row exists at all — the one question the
  // per-column entries cannot answer, since ClickHouse fills unmatched joins with type defaults
  // rather than NULL. Gated on filteringConfig.empty, which the API sets only for many-to-one
  // relations; one-to-many relations use exists/nexists on their own entry instead.
  const relationExistenceEntries = normalizedAttributes
    .filter(
      (attribute) =>
        attribute.type === "relation" &&
        attribute.filtering &&
        attribute.filteringConfig.empty &&
        attribute.relationEndpoint &&
        relationSchemas[attribute.relationEndpoint]
    )
    .map((attribute) =>
      getSchemaEntry(
        attribute.ID,
        // The relation object is not a column of its own; its expanded per-column
        // entries carry the displayable values.
        { hideInTable: true, sortable: false },
        { filterType: FilterType.RELATION, disableSelectFilters: true }
      )
    )
    .filter((entry): entry is SchemaEntry => Boolean(entry));

  const schemaWithRelationExistence = [...schema, ...relationExistenceEntries];

  const getSchemaForAccessor = getSchemaForAccessorFunction(
    schemaWithRelationExistence
  );

  return {
    schema: schemaWithRelationExistence,
    getDisplayLabel,
    attributeMap,
    attributes: normalizedAttributes,
    attributeAccessors: schemaWithRelationExistence.map(
      ({ accessor, filter }) => filter?.fieldName || accessor
    ),
    getSchemaEntry,
    getSchemaForAccessor,
    groupByRelations,
    schemaDef,
    relations: schemaRelations,
  };
};

/** Fully-built client-side schema for one schema definition, as cached. */
export type SchemaDefinitionSchemaData = Awaited<
  ReturnType<typeof fetchSchemaDefinitionSchema>
>;

/** A single cache slot for a schema definition's build. */
export type SchemaDefinitionSchemaEntry =
  | { status: "loading" }
  | { status: "success"; data: SchemaDefinitionSchemaData }
  | { status: "error"; error: APIErrorResponse }
  | { status: "notFound" };

// Stable sentinel so getEntryByName returns a referentially-stable value for
// useSyncExternalStore when a name resolves to no definition.
const NOT_FOUND_ENTRY: SchemaDefinitionSchemaEntry = Object.freeze({
  status: "notFound",
});

export interface SchemaDefinitionSchemaStore {
  /** Subscribe to cache mutations; returns an unsubscribe function. */
  subscribe: (listener: () => void) => () => void;
  /** Current cache slot for a definition ID (referentially stable). */
  getEntry: (id: string) => SchemaDefinitionSchemaEntry | undefined;
  /** Current cache slot resolved from a (globally unique) definition name. */
  getEntryByName: (name: string) => SchemaDefinitionSchemaEntry | undefined;
  /** Kick off (once) the build for a definition ID if not already cached. */
  ensureById: (id: string) => void;
  /** Resolve a definition name to an ID, then ensure its build. */
  ensureByName: (name: string) => void;
  /** Drop everything — used on logout / tenant switch, and after a schema edit. */
  clear: () => void;
  /**
   * Monotonic counter bumped by every clear(). Consumers already mounted when
   * the cache is dropped read it to know their build was discarded and must be
   * re-ensured — nothing else would restart it, leaving them loading forever.
   */
  getGeneration: () => number;
}

export const createSchemaDefinitionSchemaStore =
  (): SchemaDefinitionSchemaStore => {
    const entriesById = new Map<string, SchemaDefinitionSchemaEntry>();
    const idByName = new Map<string, string>();
    const notFoundNames = new Set<string>();
    // Stable error entries for names whose resolution request failed (the list
    // request 500s, a network blip). Storing the whole entry — not just the
    // error — keeps getEntryByName referentially stable for useSyncExternalStore
    // and lets it surface the failure instead of an eternal loading state.
    const nameErrorEntries = new Map<string, SchemaDefinitionSchemaEntry>();
    // In-flight name resolutions, de-duped so concurrent callers share one
    // request. Holds the resolved definition so its build can reuse it.
    const nameResolutions = new Map<
      string,
      Promise<SchemaDefinition | undefined>
    >();
    const listeners = new Set<() => void>();
    // Bumped by clear(). Deferred writes from requests started in an earlier
    // generation are discarded so a clear() (logout / tenant switch) can't be
    // overwritten by a slow in-flight request that resolves afterwards. Also
    // exposed via getGeneration() so mounted consumers can re-ensure their build.
    let generation = 0;

    const emit = () => {
      listeners.forEach((listener) => listener());
    };

    // Runs the build for an id, overwriting whatever is cached. Writes are
    // guarded by the global generation (bumped by clear) captured at start, so
    // a build superseded by a clear() is dropped rather than clobbering fresher
    // state.
    const startBuild = (id: string, prefetchedDef?: SchemaDefinition) => {
      const startedGeneration = generation;
      const isCurrent = () => generation === startedGeneration;

      entriesById.set(id, { status: "loading" });
      // Notify synchronously so subscribers see the loading state immediately.
      emit();

      fetchSchemaDefinitionSchema(id, prefetchedDef)
        .then((data) => {
          if (isCurrent()) entriesById.set(id, { status: "success", data });
        })
        .catch((error: unknown) => {
          if (isCurrent()) {
            entriesById.set(id, {
              status: "error",
              error: error as APIErrorResponse,
            });
          }
        })
        .finally(() => {
          if (isCurrent()) emit();
        });
    };

    const ensureById = (id: string, prefetchedDef?: SchemaDefinition) => {
      if (!id) return;

      // Reuse an in-flight (loading) or completed (success) build. A prior
      // *error* is retryable: keying on entriesById.has(id) alone would trap
      // every consumer of this app-wide cache for the whole session after a
      // single transient failure (5xx, network blip). The next ensureById call
      // (e.g. on remount) rebuilds, mirroring SWR's revalidate-on-error.
      const existing = entriesById.get(id);
      if (existing && existing.status !== "error") return;

      startBuild(id, prefetchedDef);
    };

    // Resolves a name to its full definition via a single list request,
    // recording the name -> id mapping. The definition is returned so the
    // build can reuse it instead of re-fetching by id.
    const resolveDefinition = (
      name: string
    ): Promise<SchemaDefinition | undefined> => {
      const inflight = nameResolutions.get(name);
      if (inflight) return inflight;

      const startedGeneration = generation;

      const promise = listSchemaDefinitions({
        limit: 1,
        filter: getSchemaNameFilter(name),
      })
        .then((definitions) => {
          if (generation !== startedGeneration) return undefined;

          const definition = definitions?.[0];
          if (definition) {
            idByName.set(name, definition.ID);
            // A prior failed / not-found attempt for this name is now
            // superseded (e.g. a schema created after first paint).
            notFoundNames.delete(name);
            nameErrorEntries.delete(name);

            return definition;
          }

          notFoundNames.add(name);
          nameErrorEntries.delete(name);

          return undefined;
        })
        .catch((error: unknown) => {
          // A failed name resolution must surface as an error, not an eternal
          // loading state. Record a stable error entry so getEntryByName can
          // return it. Generation-guarded so a clear() (logout / tenant switch)
          // isn't clobbered by a stale rejection.
          if (generation === startedGeneration) {
            nameErrorEntries.set(name, {
              status: "error",
              error: error as APIErrorResponse,
            });
          }

          return undefined;
        })
        .finally(() => {
          // Only retract our own in-flight marker; after a clear() a newer
          // resolution may already own this key.
          if (generation === startedGeneration) nameResolutions.delete(name);
        });

      nameResolutions.set(name, promise);

      return promise;
    };

    const ensureByName = (name: string) => {
      if (!name) return;

      const knownId = idByName.get(name);
      if (knownId) {
        ensureById(knownId);

        return;
      }

      // No notFound / error short-circuit here. A name that didn't resolve
      // before — a deleted schema, or one created after first paint in
      // super-admin flows — is retried on the next call. notFoundNames /
      // nameErrorEntries still drive getEntryByName's read (so the UI shows
      // notFound/error rather than loading), but they must not permanently
      // suppress a retry. resolveDefinition() dedupes concurrent retries, and
      // ensureByName runs once per mount (not per render), so this re-resolves
      // at most once per navigation rather than spinning.
      resolveDefinition(name).then((definition) => {
        if (definition) {
          // Reuse the definition fetched while resolving the name — no second
          // getSchemaDefinition request for the same definition.
          ensureById(definition.ID, definition);
        }

        // Always notify once a resolution settles. ensureById() above no-ops
        // *silently* when the build already exists from an earlier by-id
        // lookup, so without this emit a name resolved against an
        // already-cached id would never wake its by-name subscribers — they'd
        // sit on loading forever. Also covers the notFound / error outcomes
        // (definition is undefined), letting subscribers re-read those states.
        emit();
      });
    };

    const getEntryByName = (name: string) => {
      if (!name) return undefined;

      const id = idByName.get(name);
      if (id) return entriesById.get(id);

      if (notFoundNames.has(name)) return NOT_FOUND_ENTRY;

      const errorEntry = nameErrorEntries.get(name);
      if (errorEntry) return errorEntry;

      return undefined;
    };

    const clear = () => {
      // Invalidate any in-flight requests so their deferred writes are dropped.
      generation += 1;
      entriesById.clear();
      idByName.clear();
      notFoundNames.clear();
      nameErrorEntries.clear();
      nameResolutions.clear();
      emit();
    };

    return {
      subscribe: (listener) => {
        listeners.add(listener);

        return () => {
          listeners.delete(listener);
        };
      },
      getEntry: (id) => (id ? entriesById.get(id) : undefined),
      getEntryByName,
      ensureById,
      ensureByName,
      clear,
      getGeneration: () => generation,
    };
  };
